import type {
  GameSheetMatchup,
  GameSheetMatchupTeam,
  GameSheetTeam,
  GameSheetTeamStats,
} from "./useGameSheetBoxScore";

/**
 * Reading a team's season record out of a GameSheet response.
 *
 * Lifted out of GameDetailsFallback when the v2 countdown screen needed the
 * same record under the same crests. Two copies of these rules would have
 * drifted the first time upstream changed what it sends before puck drop,
 * which is exactly the case every function here exists to survive.
 */

/**
 * What a missing number looks like. An en dash, because the alternative was
 * the string "undefined", which is what the stats panel used to print.
 */
export const NONE = "–";

export function has(v: unknown): boolean {
  return v !== null && v !== undefined && v !== "";
}

/**
 * Wins-losses-OT/shootout-losses-ties, the season record a fan reads a team by.
 *
 * NULL-SAFE ON PURPOSE. `boxscore.<side>.record` is an EMPTY OBJECT until a
 * game is final, so every field here is undefined before puck drop, and the
 * template literal that used to build this line rendered
 * "undefined-undefined-undefined-undefined" on every scheduled game. Returns
 * null instead, so a caller can fall back to the season form in `matchup`.
 */
export function recordLine(record: GameSheetTeam["record"]): string | null {
  const { wins, losses, overtimeShootoutLosses, ties } = record ?? {};
  if (![wins, losses, overtimeShootoutLosses, ties].every(has)) return null;
  return `${wins}-${losses}-${overtimeShootoutLosses}-${ties}`;
}

/**
 * Whether a record string upstream sent carries information.
 *
 * GameSheet returns "0-0-0-0" for every seasonStandings field, and for
 * last5Overall, on an exhibition schedule. That is a placeholder for "there are
 * no standings", not a team that has lost nothing and won nothing, and reading
 * it as a record is how the RECORD row came to disagree with the record printed
 * under the crest beside it.
 */
export function isZeroed(value: string | null | undefined): boolean {
  return typeof value === "string" && /^0(\s*-\s*0)+$/.test(value.trim());
}

/**
 * Pairs each side of the matchup with the right box-score team BY ID.
 *
 * `teamA` is the home side in every response observed, and matching on that
 * rather than on position is the difference between a fan reading their own
 * team's form and reading their opponent's. The ids are right there; there is
 * no reason to trust an ordering instead.
 */
export function statsFor(
  matchup: GameSheetMatchup,
  teamId: number,
): GameSheetTeamStats | null {
  const teams = matchup?.teams;
  if (!teams) return null;
  const sides: Array<GameSheetMatchupTeam | undefined> = [teams.teamA, teams.teamB];
  return sides.find((t) => t?.id === teamId)?.stats ?? null;
}

/**
 * The record to show, preferring the season standing over this game's.
 *
 * A fan reading "RECORD" means the team's season, which is what
 * `matchup.stats.seasonStandings.thisSeasonStats` holds and what survives
 * before a game is played. The box score's own record is the fallback, and it
 * is the one that exists only afterwards.
 *
 * A ZEROED standing yields to the box score, because in exhibition play the
 * standing is the placeholder "0-0-0-0" while the box score holds the real
 * record. It is only preferred when the box score has something to say: a team
 * that genuinely has not played IS 0-0-0-0, and replacing that with a dash
 * would trade one wrong answer for another.
 */
export function seasonRecord(
  stats: GameSheetTeamStats | null | undefined,
  record: GameSheetTeam["record"],
): string {
  const standing = stats?.seasonStandings?.thisSeasonStats ?? null;
  const fromBoxScore = recordLine(record);
  if (isZeroed(standing) && fromBoxScore) return fromBoxScore;
  return standing ?? fromBoxScore ?? NONE;
}

/**
 * The same record, but for a caller that would rather print nothing than a
 * placeholder.
 *
 * The countdown screen sits on open space: a record it does not know is a line
 * it simply leaves out, per the design note that a game missing its records
 * shows no records. `seasonRecord` answers with NONE for the stats table, where
 * a row has to keep its shape; this answers with null.
 *
 * A ZEROED LINE IS ALSO NOTHING HERE, which is where this parts company with
 * seasonRecord. In exhibition and preseason play GameSheet sends "0-0-0-0" for
 * every field, and the stats table prints it because a row with a dash in it
 * still has to be a row. Under a crest, on a page with nothing else on that
 * line, "0 - 0 - 0 - 0" reads as a claim about the team rather than as the
 * absence of a standing, so it is dropped.
 */
export function optionalSeasonRecord(
  stats: GameSheetTeamStats | null | undefined,
  record: GameSheetTeam["record"],
): string | null {
  const line = seasonRecord(stats, record);
  if (line === NONE || isZeroed(line)) return null;
  return line;
}
